/* eslint-disable functional/immutable-data */

import React, { memo, useCallback, useLayoutEffect } from 'react';
import type {
  ModalProps as RNModalProps,
  PressableProps,
  ViewProps,
} from 'react-native';
import {
  Modal as RNModal,
  Pressable,
  SafeAreaView,
  StyleSheet,
  View,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import { COLORS, ELEVATIONS, RADIUS } from '@garnish/constants';
import { useBreakpointHelpers, useResponsive } from '../../hooks';
import { FadeView } from '../FadeView';
import * as ModalSubComponents from './Modal.subcomponents';
import { useModalMachine } from './modal-machine';
import type { PaletteProps } from './Modal.types';

export const Modal = (props: ModalProps) => {
  const {
    visible,
    palette,
    children,
    transparent,
    animationType,
    size,
    onRequestClose,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
    fitHeight = false,
    ...modalProps
  } = props;
  const { currentBreakpoint, match } = useResponsive();
  const conditionalAnimationType = animationType ?? match(['slide', 'fade']);

  // ─── Styles ───────────────────────────────────────────────────────────

  const dialogWrapperStyles = [
    styles.dialogWrapper,
    match([styles.dialogWrapperMobile, styles.dialogWrapperTabletUp]),
  ];

  // ─── State ────────────────────────────────────────────────────────────

  const modalMachineInitialState = visible ? 'shown' : 'dismissed';
  const { helpers: modalServiceHelpers } = useModalMachine({
    initial: modalMachineInitialState,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  });

  const syncAnimationState = useCallback(() => {
    if (animationType === 'none') return;

    if (visible) modalServiceHelpers.sendStartShowEvent();
    else modalServiceHelpers.sendStartDismissEvent();
  }, [animationType, modalServiceHelpers, visible]);

  useLayoutEffect(syncAnimationState, [syncAnimationState]);

  // ────────────────────────────────────────────────────────────────────────────────

  return (
    <RNModal
      statusBarTranslucent
      {...modalProps}
      visible={visible}
      transparent
      onRequestClose={onRequestClose}
      animationType={conditionalAnimationType}
      onShow={modalServiceHelpers.sendShowEvent}
      onDismiss={modalServiceHelpers.sendDismissEvent}
    >
      <GestureHandlerRootView
        pointerEvents="box-none"
        style={dialogWrapperStyles}
      >
        {transparent ? null : currentBreakpoint.isMobile ? (
          <FadeView
            show={Boolean(visible)}
            duration={500}
            style={StyleSheet.absoluteFill}
          >
            <ModalBackdrop onPress={onRequestClose} />
          </FadeView>
        ) : (
          <ModalBackdrop onPress={onRequestClose} />
        )}

        <ModalContainer size={size} fitHeight={fitHeight}>
          <ModalContent
            palette={palette}
            shouldFitToFill={size === 'full-screen' || size === 'x-large'}
          >
            {children}
          </ModalContent>
        </ModalContainer>
      </GestureHandlerRootView>
    </RNModal>
  );
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type ModalProps = {
  readonly size?:
    | 'x-small'
    | 'small'
    | 'medium'
    | 'large'
    | 'x-large'
    | 'full-screen';

  readonly fitHeight?: boolean;
  /**
   * Fires immediately when the {visibility} is changed to {true}.
   */
  readonly onShowing?: ModalEventHandler;

  /**
   * Fires when the "show" animation is finished and the modal is fully shown.
   */
  readonly onShown?: ModalEventHandler;

  /**
   * Fires immediately when the {visibility} is changed to {false}.
   */
  readonly onDismissing?: ModalEventHandler;

  /**
   * Fires when the "dismiss" animation is finished and the modal is fully hidden.
   */
  readonly onDismissed?: ModalEventHandler;
} & Omit<RNModalProps, 'onShow' | 'onDismiss'> &
  PaletteProps;

type ModalContainerProps = ViewProps & Pick<ModalProps, 'size' | 'fitHeight'>;

type ModalContentProps = ViewProps &
  Pick<ModalProps, 'size' | 'palette'> & {
    readonly shouldFitToFill: boolean;
  };

type ModalEventHandler = () => void;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  dialogWrapper: {
    flexGrow: 1,
    maxHeight: '100%',
  },
  dialogWrapperMobile: {
    justifyContent: 'flex-end',
  },
  dialogWrapperTabletUp: {
    justifyContent: 'center',
  },
  dialog: {
    maxWidth: '100%',
    maxHeight: 672,
    marginHorizontal: 'auto',
  },
  dialogMobile: {
    marginTop: 'auto',
    paddingTop: 80,
  },
  dialogMobileFullWidth: {
    width: '100%',
  },
  dialogTabletUp: {
    marginVertical: 'auto',
    paddingVertical: 80,
  },
  dialogSizeXSmall: { width: 400 },
  dialogSizeSmall: { width: 460 },
  dialogSizeMedium: { width: 592 },
  dialogSizeLarge: { width: 800 },
  dialogSizeXLarge: {
    width: '90%',
    height: '90%',
    maxHeight: '90%', // override default style
  },
  dialogSizeFitHeight: {
    maxHeight: '100%',
  },
  dialogSizeFullscreen: {
    width: '100%',
    height: '100%',
    maxHeight: undefined,
    paddingTop: 0,
    paddingBottom: 0,
  },
  dialogPaletteSecondary: {
    backgroundColor: COLORS.SPINACH,
  },
  dialogPaletteLime: {
    backgroundColor: COLORS.LIME,
  },

  // ========================

  content: {
    backgroundColor: COLORS.OATMEAL,
    maxHeight: '100%',
    overflow: 'hidden',
  },
  contentMobile: {
    ...ELEVATIONS['2'],
    borderTopRightRadius: RADIUS.medium,
    borderTopLeftRadius: RADIUS.medium,
  },
  contentTabletUp: {
    ...ELEVATIONS['3'],
    borderRadius: RADIUS.medium,
  },
  contentFitToFill: {
    flexGrow: 1,
  },

  // ========================

  backdrop: { backgroundColor: COLORS.OPACITY.GREEN_1.DARKER },
});

const modalDialogPaletteStyles = {
  primary: {},
  secondary: styles.dialogPaletteSecondary,
  lime: styles.dialogPaletteLime,
};

const modalDialogSizeStyles = {
  'x-small': styles.dialogSizeXSmall,
  small: styles.dialogSizeSmall,
  medium: styles.dialogSizeMedium,
  large: styles.dialogSizeLarge,
  'x-large': styles.dialogSizeXLarge,
  'full-screen': styles.dialogSizeFullscreen,
};

//
// ─── HELPERS ────────────────────────────────────────────────────────────────────
//

const ModalContainer = (props: ModalContainerProps) => {
  const { children, style, size = 'small', fitHeight, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const responsiveStyles = currentBreakpoint.isMobile
    ? styles.dialogMobile
    : styles.dialogTabletUp;

  const sizeSpecificStyles = modalDialogSizeStyles[size];
  const xsFullWidthStyle =
    currentBreakpoint.isMobile && styles.dialogMobileFullWidth;

  return (
    <View
      style={[
        styles.dialog,
        responsiveStyles,
        sizeSpecificStyles,
        xsFullWidthStyle,
        fitHeight && styles.dialogSizeFitHeight,
        style,
      ]}
      pointerEvents="box-none"
      {...restProps}
    >
      {children}
    </View>
  );
};

const ModalContent = (props: ModalContentProps) => {
  const { children, palette, shouldFitToFill, style, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const responsiveStyles = currentBreakpoint.isMobile
    ? styles.contentMobile
    : styles.contentTabletUp;
  const sizeBasedStyles = shouldFitToFill
    ? styles.contentFitToFill
    : responsiveStyles;

  const paletteSpecificStyles = palette
    ? modalDialogPaletteStyles[palette]
    : {};

  return (
    <SafeAreaView
      style={[styles.content, sizeBasedStyles, paletteSpecificStyles, style]}
      {...restProps}
    >
      {children}
    </SafeAreaView>
  );
};

const ModalBackdrop = memo((props: PressableProps) => (
  <Pressable
    testID="sg-modal-backdrop"
    {...props}
    style={[StyleSheet.absoluteFill, styles.backdrop]}
  />
));

// ────────────────────────────────────────────────────────────────────────────────

Modal.Row = ModalSubComponents.ModalRow;
Modal.CloseBtn = ModalSubComponents.ModalCloseBtn;
Modal.Headline = ModalSubComponents.ModalHeadline;
Modal.Subhead = ModalSubComponents.ModalSubhead;
Modal.FloatingCloseBtn = ModalSubComponents.ModalFloatingCloseBtn;
Modal.BodyText = ModalSubComponents.ModalBodyText;
Modal.Image = ModalSubComponents.ModalImage;
Modal.Footer = ModalSubComponents.ModalFooter;
